/**
 * Application z-index scale (single source of truth).
 *
 * Prefer Tailwind utilities from this scale (`z-panel`, `z-panel-popover`, etc.).
 * CSS custom properties (`--z-panel`, …) are applied on `:root` via
 * {@link applyZIndexCssVars} for non-Tailwind consumers (e.g. Leaflet in index.css).
 *
 * Layers are spaced so new levels can be inserted without renumbering everything.
 * (Yes, we're coding like its 1989 and BASIC and line numbers is hot stuff!)
 */
export const Z_INDEX = {
  base: 0,
  map: 10,
  mapControl: 20,
  /** Scrim behind drawers and the mobile sidebar */
  backdrop: 40,
  /** Drawer/sidebar shells and document-level popovers */
  panel: 50,
  /** Dialog scrim — above panels so modals can cover the mobile nav */
  modalBackdrop: 60,
  /** Popovers portaled into a panel (sidebar, drawer, etc.) */
  panelPopover: 70,
  /** Dialog content */
  modal: 80,
  /** Mobile hamburger — stays reachable above an open sidebar */
  mobileMenu: 90,
  tooltip: 100,
  leaflet: 300,
  leafletControl: 400,
  leafletPopup: 500,
};

/** @type {Record<string, number>} */
export const Z_INDEX_TAILWIND = {
  backdrop: Z_INDEX.backdrop,
  panel: Z_INDEX.panel,
  popover: Z_INDEX.panel,
  "modal-backdrop": Z_INDEX.modalBackdrop,
  "panel-popover": Z_INDEX.panelPopover,
  modal: Z_INDEX.modal,
  "mobile-menu": Z_INDEX.mobileMenu,
  tooltip: Z_INDEX.tooltip,
  map: Z_INDEX.map,
  "map-control": Z_INDEX.mapControl,
  leaflet: Z_INDEX.leaflet,
  "leaflet-control": Z_INDEX.leafletControl,
  "leaflet-popup": Z_INDEX.leafletPopup,
};

/**
 * Writes z-index values to CSS variables on the document root.
 * @param {Document} [doc]
 */
export function applyZIndexCssVars(doc = document) {
  const root = doc.documentElement;
  for (const [key, value] of Object.entries(Z_INDEX)) {
    const cssVar = `--z-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`;
    root.style.setProperty(cssVar, String(value));
  }
}
